import { DEFAULT_COLOR, DEFAULT_WAI_ARIA_ATTRIBUTE } from './constants';
import { isArray } from 'lodash';
import {
    CSSProperties,
    FunctionComponent,
    memo,
    ReactElement,
    useMemo,
} from 'react';

/**
 * Props for the ColorRing loader component.
 *
 * The ColorRing loader displays a circular ring with animated color segments.
 *
 * @interface ColorRingProps
 */
interface ColorRingProps {
    /** Height of the SVG (number interpreted as px). */
    height?: string | number;
    /** Width of the SVG (number interpreted as px). */
    width?: string | number;
    /** Primary color applied to the loader. */
    color?: string | string[];
    /** Accessible label announced to screen readers. */
    ariaLabel?: string;
    /** Inline style object applied to the wrapper element. */
    wrapperStyle?: CSSProperties;
    /** CSS class applied to the wrapper for custom styling. */
    wrapperClass?: string;
    /** When false, the loader is not rendered. Defaults to true. */
    visible?: boolean;
    strokeWidth?: number;
    speedScale?: number;
}

export const Loader = memo(
    (props: { loader?: ColorRingProps; full?: boolean }) => {
        const { loader = {}, full = false } = props;

        const loaderProps: Partial<ColorRingProps> = useMemo(
            () => ({
                strokeWidth: 5,
                color: '#eb4247',
                speedScale: 1.5,
                ...loader,
            }),
            [loader]
        );

        const sizes = useMemo(() => {
            if (full) {
                return { width: '100vw', height: '100vh' };
            }

            return {
                height: '100%',
                width: '100%',
            };
        }, [full]);

        return (
            <div
                style={{
                    ...sizes,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'white',
                }}
            >
                <ColorRing {...loaderProps} />
            </div>
        );
    }
);

export const ColorRing: FunctionComponent<ColorRingProps> = memo(
    (props: ColorRingProps): ReactElement | null => {
        const {
            visible = true,
            width = '80',
            height = '80',
            wrapperClass = 'color-ring',
            wrapperStyle = {},
            ariaLabel = 'color-ring-loading',
            strokeWidth = 9,
            speedScale = 1,
        } = props;

        const colors: string[] = useMemo((): string[] => {
            if (isArray(props.color)) return props.color;
            return new Array(5).fill(props.color ?? DEFAULT_COLOR);
        }, [props.color]);

        if (!visible) return null;

        return (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                width={width}
                height={height}
                viewBox="0 0 100 100"
                preserveAspectRatio="xMidYMid"
                className={wrapperClass}
                style={wrapperStyle}
                aria-label={ariaLabel}
                data-testid="color-ring-svg"
                {...DEFAULT_WAI_ARIA_ATTRIBUTE}
            >
                <defs>
                    <mask id="ldio-4offds5dlws-mask">
                        <circle
                            cx="50"
                            cy="50"
                            r="26"
                            stroke="#fff"
                            strokeLinecap="round"
                            strokeDasharray="122.52211349000194 40.840704496667314"
                            strokeWidth={strokeWidth}
                            transform="rotate(198.018 50 50)"
                        >
                            <animateTransform
                                attributeName="transform"
                                type="rotate"
                                values="0 50 50;360 50 50"
                                keyTimes="0;1"
                                dur={`${1 / speedScale}s`}
                                repeatCount="indefinite"
                            ></animateTransform>
                        </circle>
                    </mask>
                </defs>
                <g mask="url(#ldio-4offds5dlws-mask)">
                    <rect
                        x="14.5"
                        y="0"
                        width="15"
                        height="100"
                        fill={colors[0]}
                    >
                        <animate
                            attributeName="fill"
                            values={colors.join(';').toString()}
                            keyTimes="0;0.25;0.5;0.75;1"
                            dur={`${1 / speedScale}s`}
                            repeatCount="indefinite"
                            begin="-0.8s"
                        ></animate>
                    </rect>
                    <rect
                        x="28.5"
                        y="0"
                        width="15"
                        height="100"
                        fill={colors[1]}
                    >
                        <animate
                            attributeName="fill"
                            values={colors.join(';').toString()}
                            keyTimes="0;0.25;0.5;0.75;1"
                            dur={`${1 / speedScale}s`}
                            repeatCount="indefinite"
                            begin="-0.6s"
                        ></animate>
                    </rect>
                    <rect
                        x="42.5"
                        y="0"
                        width="15"
                        height="100"
                        fill={colors[2]}
                    >
                        <animate
                            attributeName="fill"
                            values={colors.join(';').toString()}
                            keyTimes="0;0.25;0.5;0.75;1"
                            dur={`${1 / speedScale}s`}
                            repeatCount="indefinite"
                            begin="-0.4s"
                        ></animate>
                    </rect>
                    <rect
                        x="56.5"
                        y="0"
                        width="15"
                        height="100"
                        fill={colors[3]}
                    >
                        <animate
                            attributeName="fill"
                            values={colors.join(';').toString()}
                            keyTimes="0;0.25;0.5;0.75;1"
                            dur={`${1 / speedScale}s`}
                            repeatCount="indefinite"
                            begin="-0.2s"
                        ></animate>
                    </rect>
                    <rect
                        x="70.5"
                        y="0"
                        width="15"
                        height="100"
                        fill={colors[4]}
                    >
                        <animate
                            attributeName="fill"
                            values={colors.join(';').toString()}
                            keyTimes="0;0.25;0.5;0.75;1"
                            dur={`${1 / speedScale}s`}
                            repeatCount="indefinite"
                            begin="0s"
                        ></animate>
                    </rect>
                </g>
            </svg>
        );
    }
);
